import { create } from "zustand"
import { WebViewerInstance } from "@pdftron/webviewer"
import ViewerNavigationService from "services/viewerNavigation.service"
import AnnotationService from "services/annotations.service"
import type { PDFInstance } from "types/viewer.type"

// The two modes a single Quotations-tab WebViewer instance can be in. See
// local-docs/plans/single-instance-quotation-viewer.md (#1119).
export type TViewerMode = "preview" | "editor"

type ViewerStore = {
	instance: PDFInstance | null
	isNavigating: boolean
	isFeedbackPanelOpen: boolean
	isQuotationFeedbackOpen: boolean
	isQuotationSettingsOpen: boolean
	// True while the fullscreen quotation viewer (preview or editor) is open. The FileInfo drawer
	// reads this to stop closing on Escape while the viewer covers it — the viewer owns Escape.
	isQuotationViewerOpen: boolean
	setQuotationViewerOpen: (open: boolean) => void
	setPreviewInstance: (instance: PDFInstance | WebViewerInstance) => void
	clearPreviewInstance: () => void
	registerQuotationInstance: (instance: PDFInstance | WebViewerInstance, mode: TViewerMode) => void
	setNavigating: (navigating: boolean) => void
	openFeedbackPanel: () => void
	closeFeedbackPanel: () => void
	openQuotationFeedback: () => void
	closeQuotationFeedback: () => void
	openQuotationSettings: () => void
	closeQuotationSettings: () => void
}

// Add viewer instance and navigation function
export const useViewerStore = create<ViewerStore>((set) => ({
	instance: null,
	isNavigating: false,
	isFeedbackPanelOpen: false,
	isQuotationFeedbackOpen: false,
	isQuotationSettingsOpen: false,
	isQuotationViewerOpen: false,
	// Used by the read-only document preview (All Files → Documents). It registers ONLY
	// the navigation service (required by the AI Agent page-reference navigation) and the
	// store instance (required by DocumentModal's resize handling). It deliberately does
	// NOT register AnnotationService — that singleton belongs to the custom quotation
	// editor, and pointing it at the preview hijacks it (promoteUserToAdmin, cached
	// annotations re-rendering on documentLoaded). See PR #1118.
	setPreviewInstance: (instance) => {
		const pdfInstance = instance as PDFInstance
		set({ instance: pdfInstance })
		ViewerNavigationService.setInstance(pdfInstance)
	},
	clearPreviewInstance: () => {
		set({ instance: null })
		ViewerNavigationService.cleanup()
	},
	// Unified registration for the SINGLE Quotations-tab viewer instance (preview + editor
	// share one instance — see #1119). Always publishes the instance to the store and the
	// navigation service. AnnotationService is attached only in editor mode (it owns
	// annotation rendering/selection); switching to preview detaches it so its listeners and
	// cached metadata don't act on the read-only preview.
	registerQuotationInstance: (instance, mode) => {
		const pdfInstance = instance as PDFInstance
		set({ instance: pdfInstance })
		ViewerNavigationService.setInstance(pdfInstance)
		if (mode === "editor") {
			AnnotationService.setInstance(pdfInstance)
		} else {
			AnnotationService.cleanup()
		}
	},
	setNavigating: (navigating) => set({ isNavigating: navigating }),
	setQuotationViewerOpen: (open) => set({ isQuotationViewerOpen: open }),
	openFeedbackPanel: () => set({ isFeedbackPanelOpen: true }),
	closeFeedbackPanel: () => set({ isFeedbackPanelOpen: false }),
	openQuotationFeedback: () => set({ isQuotationFeedbackOpen: true }),
	closeQuotationFeedback: () => set({ isQuotationFeedbackOpen: false }),
	openQuotationSettings: () => set({ isQuotationSettingsOpen: true }),
	closeQuotationSettings: () => set({ isQuotationSettingsOpen: false }),
}))
